// Oct14 formDataUtils.js (with safe prune before compression)
//import { compressDataForDraft, saveDraftFormToFirestore } from './saveDraftFormToFirestore';
//import { safeGetItem } from './storageUtils'; // Ensure correct relative path
//import { auth } from '../firebase'; // Import Firebase authentication

import { compressDataForDraft } from './saveDraftFormToFirestore';
import { safeGetItem } from './storageUtils';
import { FIELD_TYPE_CALCULATED } from '../components/Fields/constants/fieldTypes';

// Utility to selectively serialize layout configuration
export const serializeLayoutConfig = (layoutConfig) => {
  const seen = new WeakSet();
  return JSON.stringify(layoutConfig, (key, value) => {
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) {
        return; // Skip circular references
      }
      seen.add(value);

      // Only serialize essential properties necessary for UI construction
      if (value && typeof value === 'object' && value.id && value.type) {
        return {
          id: value.id,
          type: value.type,
          label: value.label, // Often used for display
          placeholder: value.placeholder, // For inputs
          required: value.required, // For validating inputs
          index: value.index, // For order or specific placements
          ufi: value.ufi, // Include the UFI to ensure field identity is maintained
          // Include other properties as needed based on your component's usage
        };
      }
    }
    return value;
  });
};

export const prepareFormDataForFirestore = async ({
  formId,
  formDetails,
  formTitle,
  formDescription,
  layoutConfig,
  dropdownDependencies,
  submissionSettings,
  selectedTheme,
  colors,
  fonts,
  submitButtonStyle,
  fieldVariant,
  organizationId,
  userId, //  Pass explicitly from context (currentUser.uid)

  // Builder UI mode: 'auto' | 'published' | 'draftOverlay'
  // Aligns with utils/formVersionResolver.normalizeBuilderViewMode()
  viewMode = 'auto',

  // Optional overrides (lets caller avoid storage reads during refactor)
  // Pass these from FormPublishContext / FormSaveContext when available.
  fieldsMetadataOverride = null,
  dropdownOptionsOverride = null,
  submissionSettingsOverride = null,

  status = 'draft', // Default status is 'draft', can be 'published'
  // tells Firestore layer how to store draft writes
  // - 'draftOverlay' keeps published doc published and stores working copy under draftOverlay.*
  // - 'draft' overwrites root doc to draft (legacy)
  saveMode = 'draftOverlay',
}) => {


  console.log(`[formDataUtils] Preparing form data for Firestore with status: ${status}...`);

  if (!formDetails) {
    console.error('[formDataUtils] ERROR: formDetails is undefined! Please check useFormContext.');
    return null;
  }

  if (!formTitle || !formDescription) {
    console.warn('[formDataUtils] Warning: formTitle or formDescription is missing before constructing updatedFormDetails.');
  }

  // ────────────────────────────────────────────────────────────────
  // Sanitize base formDetails so we never re-write blobs/overlay keys
  // into metadata. Matches FormSaveContext separation.
  // ────────────────────────────────────────────────────────────────
  const {
    // strip working-copy payload + related index fields
    draftOverlay: _draftOverlay,
    draftOverlayStatus: _draftOverlayStatus,
    draftOverlaySavedAt: _draftOverlaySavedAt,
    draftOverlaySavedBy: _draftOverlaySavedBy,

    // strip compressed/root blob fields (these should not live in metadata)
    compressedData: _compressedDataBlob,
    layoutConfig: _layoutConfigBlob,
    theme: _themeBlob,
    fieldsMetadata: _fieldsMetadataBlob,
    dropdownOptions: _dropdownOptionsBlob,
    dropdownDependencies: _dropdownDependenciesBlob,
    submissionSettings: _submissionSettingsBlob,

    // keep the rest as "base metadata"
    ...baseFormDetails
  } = formDetails || {};

  // Ensure we use the latest title and description even if formDetails is stale
  const updatedFormDetails = {
    ...baseFormDetails, // ✅ Use sanitized metadata only
    title: formTitle, // Ensure latest title
    description: formDescription, // Ensure latest description
    previewToken: baseFormDetails?.previewToken,
    createdBy: baseFormDetails?.createdBy || userId,
    owner: baseFormDetails?.owner || userId, // Ensure correct user tracking
    organizationId: organizationId || '',
    isTemplate: baseFormDetails?.isTemplate || false,
    isPublic: baseFormDetails?.isPublic || false,
    lastModified: new Date(),
  };


  console.log('[formDataUtils] Updated Form Details:', updatedFormDetails);
  if (!updatedFormDetails.title || !updatedFormDetails.description) {
    console.error('[formDataUtils] ERROR: updatedFormDetails is missing title or description!');
  }

  // ────────────────────────────────────────────────────────────────
  // Mode-aware storage keys (align with FormLayoutContext + FieldMetadataContext)
  // - viewMode is the builder UI selection: 'auto' | 'published' | 'draftOverlay'
  // - This util is about what we COMPRESS + WRITE (not Firestore doc resolution).
  // ────────────────────────────────────────────────────────────────
const mode = String(viewMode || 'auto').trim().toLowerCase();
const modeSuffix =
  (mode === 'draftoverlay' || mode === 'draft_overlay' || mode === 'draft-overlay' || mode === 'overlay' || mode === 'draft')
    ? 'draftOverlay'
    : (mode === 'published' ? 'published' : 'auto');


  const metaModeKey = `form_${formId}_fieldsMetadata__${modeSuffix}`;
  const depsModeKey = `form_${formId}_dropdownDependencies__${modeSuffix}`;

  // Prefer the mode-aware cache first, then fall back to legacy.
  // This keeps publish/save consistent with what the builder is actually using.
  const optsModeKey = `form_${formId}_dropdownOptions__${modeSuffix}`;
  const optsKey     = `form_${formId}_dropdownOptions`; // legacy fallback (kept during migration)

  const submKey     = `submissionSettings_${formId}`;


  // Fetch field metadata (override → mode-aware storage → legacy storage)
  const storedFieldsMetadata =
    (fieldsMetadataOverride && typeof fieldsMetadataOverride === 'object' && Object.keys(fieldsMetadataOverride).length > 0)
      ? fieldsMetadataOverride
      : (
          safeGetItem(metaModeKey) ||
          safeGetItem(`form_${formId}_fieldsMetadata`) ||
          {}
        );

  console.log('[formDataUtils] Retrieved field metadata for saving:', storedFieldsMetadata);

  // Fetch dropdown options (override → mode-aware storage → legacy storage)
  // NOTE: MultiAnswerFieldContext now uses mode-aware keys:
  //   form_${formId}_dropdownOptions__${effectiveViewMode}
  // This util uses modeSuffix (derived from viewMode) to match that behavior.
  const storedDropdownOptions =
    (dropdownOptionsOverride && typeof dropdownOptionsOverride === 'object' && Object.keys(dropdownOptionsOverride).length > 0)
      ? dropdownOptionsOverride
      : (
          safeGetItem(optsModeKey) ||
          safeGetItem(optsKey) ||
          safeGetItem(`form_${formId}_dropdownOptions`) ||
          {}
        );

  console.log('[formDataUtils] Retrieved dropdown options for saving:', storedDropdownOptions);

//  Fetch dropdown dependencies

// 1) What we have cached in storage
const depsFromStorage =
  safeGetItem(depsModeKey) ||
  safeGetItem(`form_${formId}_dropdownDependencies`) ||
  null;


// 2) What FormPublishContext passed in (live from DropdownDependenciesContext)
const hasContextDeps =
  dropdownDependencies &&
  typeof dropdownDependencies === 'object' &&
  !Array.isArray(dropdownDependencies) &&
  Object.keys(dropdownDependencies).length > 0;

const depsFromContext = hasContextDeps ? dropdownDependencies : null;

// 3) Decide which to use – **context wins**, then storage, then {}
const storedDropdownDependencies = depsFromContext || depsFromStorage || {};

console.log(
  '[formDataUtils] dropdownDependencies (raw) at prepare time:',
  JSON.stringify(
    {
      fromStorage        : depsFromStorage,
      fromContextPropArg : dropdownDependencies || null,
      chosenForPrepare   : storedDropdownDependencies,
    },
    null,
    2
  )
);



// Mode-aware submissionSettings keys (aligned with FormSubmissionSettingsContext)
const submModeKey  = `submissionSettings_${formId}__${modeSuffix}`;
const submPubKey   = `submissionSettings_${formId}__published`;
const submLegacyKey = `submissionSettings_${formId}`;

console.log('[formDataUtils] Mode-aware key selection at prepare time:', {
  viewMode,
  modeSuffix,
  metaModeKey,
  depsModeKey,
  optsModeKey, 
  optsKey,
  submModeKey,
  submPubKey,
  submLegacyKey,
});


// Fetch submission settings (override → caller param → mode-aware storage → legacy storage)
const storedSubmissionSettings =
  (submissionSettingsOverride && typeof submissionSettingsOverride === 'object' && Object.keys(submissionSettingsOverride).length > 0)
    ? submissionSettingsOverride
    : (
        (submissionSettings && typeof submissionSettings === 'object' && Object.keys(submissionSettings).length > 0)
          ? submissionSettings
          : (
            safeGetItem(submModeKey) ||
            safeGetItem(submPubKey)  ||
            safeGetItem(submLegacyKey) ||
            {}
            )
      );

console.log('[formDataUtils] Retrieved submission settings:', storedSubmissionSettings);


  //  Prepare theme data
  const themeData = {
    selectedTheme: selectedTheme || 'Paper',
    colors: colors || {},
    fonts: fonts || {},
    submitButtonStyle: submitButtonStyle || {},
    fieldVariant: fieldVariant || 'outlined',
    lastUpdated: new Date().toISOString(),
  };
  console.log('[formDataUtils] Theme data before compression:', themeData);

  //  Serialize layoutConfig before compression
  let serializedLayoutConfig;
  try {
    serializedLayoutConfig = JSON.parse(serializeLayoutConfig(layoutConfig));
    console.log('[formDataUtils] Serialized LayoutConfig:', serializedLayoutConfig);
  } catch (serializationError) {
    console.error('[formDataUtils] Error serializing layoutConfig:', serializationError);
    return null;
  }

  /* ────────────────────────────────────────────────────────────────
     ★ PRUNE (layout-aligned): prevent deleted/obsolete fields from
       being re-published via stale storage snapshots.
     - We do NOT mutate your stored objects; we create pruned copies
       right before compression.
     - Applies to all forms (AUF + regular).
     ──────────────────────────────────────────────────────────────── */
  let fieldsMetadataForCompression       = storedFieldsMetadata;
  let dropdownDependenciesForCompression = storedDropdownDependencies;
  let dropdownOptionsForCompression      = storedDropdownOptions;

  try {
    // Build the set of fieldIds that actually exist in the current layout
    const idsInLayout = new Set(
      Object.values(serializedLayoutConfig || {})
        .flat()
        .map((cell) => cell?.id)
        .filter(Boolean)
    );

    // 1) Prune fieldsMetadata by layout (fieldId-based)
    const prunedFieldsMetadata = Object.fromEntries(
      Object.entries(storedFieldsMetadata || {}).filter(([id, meta]) => {
        // Keep real fields that exist in the current layout
        if (idsInLayout.has(id)) return true;

        // Keep calculated fields even though they are not part of layoutConfig.
        // These power Display Table "New calculated field" columns (e.g., "UTM")
        // and must be preserved in Firestore so submit-time evaluation works
        // on the published viewer route.
        const t = meta?.type;
        if (t === FIELD_TYPE_CALCULATED || t === 'calculated') return true;

        return false;
      })
    );

    // OPTIONAL DEBUG: confirm which calculated metas survived PRUNE
    const keptCalculated = Object.entries(prunedFieldsMetadata || {})
      .filter(([, m]) => (m?.type === FIELD_TYPE_CALCULATED || m?.type === 'calculated'))
      .map(([id]) => id);

    console.log('[formDataUtils:PRUNE] kept calculated metas:', keptCalculated);


    if (Object.keys(prunedFieldsMetadata).length !== Object.keys(storedFieldsMetadata).length) {
      console.log('[formDataUtils] PRUNE fieldsMetadata to match layout:', {
        before: Object.keys(storedFieldsMetadata).length,
        after : Object.keys(prunedFieldsMetadata).length,
      });
    }

    // 2) Determine which option sets (optionsSetUFI) are still in use by fields in the layout
    const activeOptionSetUFIs = new Set(
      Object.values(prunedFieldsMetadata || {})
        .map((m) => m?.optionsSetUFI)
        .filter(Boolean)
    );

    console.log('[formDataUtils:PRUNE] activeOptionSetUFIs:', Array.from(activeOptionSetUFIs));

    // Build a global set of all valid optionIds across all sets (for dependentOptionIds cleanup)
    const allValidOptionIds = new Set(
      Object.values(storedDropdownOptions || {})
        .flatMap((optsArr) =>
          Array.isArray(optsArr)
            ? optsArr.map((o) => o?.optionId).filter(Boolean)
            : []
        )
    );

    console.log(
      '[formDataUtils:PRUNE] allValidOptionIds:',
      Array.from(allValidOptionIds)
    );

    // 3) Prune dropdownDependencies:
    //    - Keep only option sets (optionsSetUFI) still referenced by fields in the layout
    //    - Within each set, keep only optionIds that still exist in that set’s options
    //    - For each dependency, filter dependentOptionIds to only those optionIds that still exist anywhere
    let prunedDropdownDependencies = {};

    if (
      storedDropdownDependencies &&
      typeof storedDropdownDependencies === 'object' &&
      !Array.isArray(storedDropdownDependencies)
    ) {
      // If we have no active option sets but do have dependencies, be conservative and skip pruning
      const shouldPruneDeps =
        activeOptionSetUFIs.size > 0 && Object.keys(storedDropdownDependencies).length > 0;

      if (!shouldPruneDeps) {
        prunedDropdownDependencies = storedDropdownDependencies;
        console.log('[formDataUtils:PRUNE] Skipping dependency prune (no active option sets).');
      } else {
        prunedDropdownDependencies = Object.fromEntries(
          Object.entries(storedDropdownDependencies).reduce(
            (acc, [optionsSetUFI, perOptionMap]) => {
              console.log(
                '[formDataUtils:PRUNE] Inspecting optionsSetUFI:',
                optionsSetUFI,
                'with keys:',
                Object.keys(perOptionMap || {})
              );

              // Drop entire set if it's no longer referenced by any field in the current layout
              if (!activeOptionSetUFIs.has(optionsSetUFI)) {
                console.log(
                  '[formDataUtils:PRUNE] Dropping entire dependency set (no active field uses it):',
                  optionsSetUFI
                );
                return acc;
              }

              const optionsForSet = storedDropdownOptions?.[optionsSetUFI];
              const validOptionIdsForSet =
                Array.isArray(optionsForSet)
                  ? new Set(
                      optionsForSet
                        .map((opt) => opt?.optionId)
                        .filter(Boolean)
                    )
                  : null;

              console.log(
                '[formDataUtils:PRUNE] validOptionIdsForSet for',
                optionsSetUFI,
                ':',
                validOptionIdsForSet ? Array.from(validOptionIdsForSet) : '(none – keeping raw perOptionMap)'
              );

              // If we have no options for this set yet (e.g. they load later),
              // keep dependencies as-is to avoid destructive pruning.
              if (!validOptionIdsForSet) {
                acc.push([optionsSetUFI, perOptionMap || {}]);
                return acc;
              }

              const cleanedPerOption = Object.fromEntries(
                Object.entries(perOptionMap || {}).map(([optionId, conf]) => {
                  // Drop entries for optionIds that no longer exist in this set
                  if (!validOptionIdsForSet.has(optionId)) {
                    console.log(
                      '[formDataUtils:PRUNE] Dropping dependency entry for removed optionId',
                      optionId,
                      'in optionsSetUFI',
                      optionsSetUFI
                    );
                    return [null, null]; // filtered out below
                  }

                  let nextConf = conf;

                  try {
                    if (
                      conf &&
                      typeof conf === 'object' &&
                      Array.isArray(conf.dependentOptionIds)
                    ) {
                      console.log(
                        '[formDataUtils:PRUNE] BEFORE filtering deps for',
                        optionsSetUFI,
                        optionId,
                        'dependentOptionIds =',
                        conf.dependentOptionIds
                      );

                      const filteredDeps = conf.dependentOptionIds.filter((depId) =>
                        allValidOptionIds.has(depId)
                      );

                      console.log(
                        '[formDataUtils:PRUNE] AFTER  filtering deps for',
                        optionsSetUFI,
                        optionId,
                        'filteredDeps =',
                        filteredDeps
                      );

                      // Always keep the entry, even if deps become empty; this matches
                      // how DropdownDependenciesContext initialises { dependentOptionIds: [] }
                      if (filteredDeps.length !== conf.dependentOptionIds.length) {
                        nextConf = { ...conf, dependentOptionIds: filteredDeps };
                      }
                    }
                  } catch (e) {
                    console.warn(
                      '[formDataUtils:PRUNE] Error while filtering deps for',
                      optionsSetUFI,
                      optionId,
                      ':',
                      e?.message || e
                    );
                    nextConf = conf;
                  }

                  return [optionId, nextConf];
                })
              );

              // Remove the [null, null] entries introduced above
              const finalPerOption = Object.fromEntries(
                Object.entries(cleanedPerOption).filter(([k]) => k !== 'null' && k !== null)
              );

              acc.push([optionsSetUFI, finalPerOption]);
              return acc;
            },
            []
          )
        );

        if (
          Object.keys(prunedDropdownDependencies).length !==
          Object.keys(storedDropdownDependencies).length
        ) {
          console.log(
            '[formDataUtils:PRUNE] PRUNE dropdownDependencies to match active option sets and existing options:',
            {
              before: Object.keys(storedDropdownDependencies).length,
              after : Object.keys(prunedDropdownDependencies).length,
            }
          );
        }

        // 🔍 Extra sanity log: show a focused view on the set you're debugging
//        console.log(
//          '[formDataUtils:PRUNE] prunedDropdownDependencies snapshot for formId',
//          formId,
//          ':',
//          JSON.stringify(prunedDropdownDependencies, null, 2)
//        );
      }
    } else {
      prunedDropdownDependencies = storedDropdownDependencies || {};
      console.log('[formDataUtils:PRUNE] No structured dropdownDependencies – using raw or empty object.');
    }

    // 4) Prune dropdownOptions to only the sets still referenced by fields in the layout
    const prunedDropdownOptions = Object.fromEntries(
      Object.entries(storedDropdownOptions || {}).filter(([setUfi]) =>
        activeOptionSetUFIs.has(setUfi)
      )
    );

    if (Object.keys(prunedDropdownOptions).length !== Object.keys(storedDropdownOptions).length) {
//      console.log('[formDataUtils:PRUNE] PRUNE dropdownOptions to referenced sets only:', {
//        before: Object.keys(storedDropdownOptions).length,
//        after : Object.keys(prunedDropdownOptions).length,
//      });
    }

    // Use the pruned copies for compression
    fieldsMetadataForCompression       = prunedFieldsMetadata;
    dropdownDependenciesForCompression = prunedDropdownDependencies;
    dropdownOptionsForCompression      = prunedDropdownOptions;

//    console.log(
//      '[formDataUtils:PRUNE] Final dropdownDependenciesForCompression:',
//      JSON.stringify(dropdownDependenciesForCompression, null, 2)
//    );


  } catch (pruneErr) {
    console.warn('[formDataUtils] PRUNE skipped (non-fatal):', pruneErr?.message || pruneErr);
    // fall back to originals if anything goes wrong
    fieldsMetadataForCompression       = storedFieldsMetadata;
    dropdownDependenciesForCompression = storedDropdownDependencies;
    dropdownOptionsForCompression      = storedDropdownOptions;
  }

  // Compress Data Before Saving
  let compressedLayoutConfig,
    compressedThemeData,
    compressedFieldsMetadata,
    compressedDropdownOptions,
    compressedDropdownDependencies,
    compressedSubmissionSettings;
  try {
    compressedLayoutConfig = await compressDataForDraft(serializedLayoutConfig);
    compressedThemeData    = await compressDataForDraft(themeData);

    // ★ Use pruned maps here
    compressedFieldsMetadata       = await compressDataForDraft(fieldsMetadataForCompression);
    compressedDropdownOptions      = await compressDataForDraft(dropdownOptionsForCompression);
    compressedDropdownDependencies = await compressDataForDraft(dropdownDependenciesForCompression);

    compressedSubmissionSettings = await compressDataForDraft(storedSubmissionSettings);

    console.log('[formDataUtils] Compressed Data:', {
      compressedLayoutConfig,
      compressedThemeData,
      compressedFieldsMetadata,
      compressedDropdownOptions,
      compressedDropdownDependencies,
      compressedSubmissionSettings,
    });
  } catch (compressionError) {
    console.error('[formDataUtils] Error compressing data:', compressionError);
    return null;
  }

  // Prepare the final draft data before saving
  const draftData = {
    formId,
    formDetails: updatedFormDetails,
    layoutConfig: compressedLayoutConfig,
    fieldsMetadata: compressedFieldsMetadata,
    dropdownOptions: compressedDropdownOptions,
    dropdownDependencies: compressedDropdownDependencies,
    submissionSettings: compressedSubmissionSettings,
    theme: compressedThemeData,
    status,
  };

  console.log('[formDataUtils] Final draftData before compression:', draftData);

  if (!draftData.formDetails.title || !draftData.formDetails.description) {
    console.error(
      '[formDataUtils] CRITICAL: draftData.formDetails is missing title or description right before saving!'
    );
  }

  //  Compress entire draftData (WITHOUT formDetails)
  let compressedDraftData;
  try {
    compressedDraftData = await compressDataForDraft({
      formId,
      layoutConfig: compressedLayoutConfig,
      theme: compressedThemeData,
      fieldsMetadata: compressedFieldsMetadata,
      dropdownOptions: compressedDropdownOptions,
      dropdownDependencies: compressedDropdownDependencies,
      submissionSettings: compressedSubmissionSettings,
      status,
    });

    console.log('[formDataUtils] Compressed Draft Data:', compressedDraftData);
  } catch (compressionError) {
    console.error('[formDataUtils] Error compressing final draft data:', compressionError);
    return null;
  }

  //  Return data in the correct format expected by Firestore functions
  return {
    formId,
    compressedDraftData, // Compressed entire form data
    compressedLayoutConfig, // Compressed layout config
    compressedThemeData, // Compressed theme data
    compressedFieldsMetadata, // Compressed field metadata (now pruned to layout)
    compressedDropdownOptions,
    compressedDropdownDependencies,
    compressedSubmissionSettings,
    userId, //  Correct userId from function argument
    organizationId,
    formDetails: updatedFormDetails, // Ensure updated form details are returned
    saveMode,
  };
};
